import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink, Smartphone, Trash2 } from 'lucide-react';

export type LoyaltyWalletForm = {
  apple_wallet_enabled: boolean;
  apple_wallet_pass_type_id: string;
  apple_wallet_team_id: string;
  apple_wallet_org_name: string;
  apple_wallet_cert: string;
  apple_wallet_key: string;
  apple_wallet_key_password: string;
  apple_wallet_wwdr: string;
  clear_apple_wallet_cert: boolean;
  clear_apple_wallet_key: boolean;
  clear_apple_wallet_wwdr: boolean;
  google_wallet_enabled: boolean;
  google_wallet_issuer_id: string;
  google_wallet_class_suffix: string;
  google_wallet_sa_json: string;
  clear_google_wallet_sa_json: boolean;
};

export type LoyaltyWalletMeta = {
  apple_wallet_cert_set?: boolean;
  apple_wallet_key_set?: boolean;
  apple_wallet_wwdr_set?: boolean;
  apple_wallet_key_password_set?: boolean;
  google_wallet_sa_json_set?: boolean;
};

type Props = {
  value: LoyaltyWalletForm;
  meta?: LoyaltyWalletMeta;
  onChange: (patch: Partial<LoyaltyWalletForm>) => void;
};

function Guide({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80">
      <button
        type="button"
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
      </button>
      {open ? <div className="px-3 pb-3 text-xs text-slate-600 space-y-2 leading-relaxed">{children}</div> : null}
    </div>
  );
}

function SecretField({
  label,
  placeholder,
  value,
  isSet,
  clearing,
  onChange,
  onClear,
  rows = 4,
  mono = true,
}: {
  label: string;
  placeholder: string;
  value: string;
  isSet?: boolean;
  clearing?: boolean;
  onChange: (v: string) => void;
  onClear: () => void;
  rows?: number;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-1">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        {isSet && !clearing ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5">
            Saved
          </span>
        ) : null}
        {clearing ? (
          <span className="text-[10px] font-semibold uppercase tracking-wide text-rose-700 bg-rose-50 border border-rose-100 rounded px-1.5 py-0.5">
            Will clear on save
          </span>
        ) : null}
      </div>
      <textarea
        className={`input-field text-xs w-full ${mono ? 'font-mono' : ''}`}
        rows={rows}
        placeholder={isSet && !value ? placeholder : placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      {isSet ? (
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1 text-[11px] text-rose-600 hover:underline"
          onClick={onClear}
        >
          <Trash2 className="w-3 h-3" /> Clear saved value
        </button>
      ) : null}
    </div>
  );
}

/**
 * Per-tenant Apple Wallet + Google Wallet credentials with setup directions.
 */
export default function LoyaltyWalletSettings({ value, meta, onChange }: Props) {
  return (
    <section className="space-y-4 border-t border-gray-100 pt-4">
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 flex items-center gap-1.5">
          <Smartphone className="w-3.5 h-3.5" /> Mobile wallets
        </h3>
        <p className="text-xs text-gray-500 mt-1 max-w-2xl">
          Optional. When enabled and credentials are filled, members can add their loyalty card to{' '}
          <strong>Apple Wallet</strong> (iPhone) or <strong>Google Wallet</strong> (Android) from the
          customer portal.
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Apple */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Apple Wallet (iPhone)</h4>
              <p className="text-[11px] text-gray-500">Requires an Apple Developer account</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={value.apple_wallet_enabled}
              onClick={() => onChange({ apple_wallet_enabled: !value.apple_wallet_enabled })}
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                value.apple_wallet_enabled ? 'bg-gray-900' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                  value.apple_wallet_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <Guide title="How to get Apple credentials">
            <ol className="list-decimal pl-4 space-y-1.5">
              <li>
                Sign in to{' '}
                <a
                  className="text-blue-700 hover:underline inline-flex items-center gap-0.5"
                  href="https://developer.apple.com/account"
                  target="_blank"
                  rel="noreferrer"
                >
                  Apple Developer <ExternalLink className="w-3 h-3" />
                </a>{' '}
                (paid membership).
              </li>
              <li>
                Go to <strong>Certificates, Identifiers &amp; Profiles → Identifiers → Pass Type IDs</strong> and
                create an ID such as <code className="bg-white px-1 rounded border">pass.com.yourshop.loyalty</code>.
              </li>
              <li>
                Create a <strong>Pass Type ID certificate</strong> for that identifier, download the{' '}
                <code className="bg-white px-1 rounded border">.cer</code>, install it in Keychain (Mac), then
                export the certificate + private key as a <code className="bg-white px-1 rounded border">.p12</code>.
              </li>
              <li>
                Convert to PEM on a Mac/Linux terminal:
                <pre className="mt-1 p-2 rounded bg-white border text-[10px] overflow-x-auto whitespace-pre-wrap">{`openssl pkcs12 -in Certificates.p12 -clcerts -nokeys -out cert.pem
openssl pkcs12 -in Certificates.p12 -nocerts -nodes -out key.pem`}</pre>
              </li>
              <li>
                Download Apple’s WWDR intermediate (G4) from{' '}
                <a
                  className="text-blue-700 hover:underline inline-flex items-center gap-0.5"
                  href="https://www.apple.com/certificateauthority/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Apple Certificate Authority <ExternalLink className="w-3 h-3" />
                </a>{' '}
                and convert if needed:
                <pre className="mt-1 p-2 rounded bg-white border text-[10px] overflow-x-auto">{`openssl x509 -inform DER -in AppleWWDRCAG4.cer -out wwdr.pem`}</pre>
              </li>
              <li>
                Copy your <strong>Team ID</strong> from Membership details, paste the PEM files below, enable Apple
                Wallet, and save.
              </li>
            </ol>
          </Guide>

          {value.apple_wallet_enabled ? (
            <div className="space-y-2.5">
              <div>
                <label className="text-xs font-medium text-gray-600">Pass Type ID</label>
                <input
                  className="input-field text-sm w-full mt-1 font-mono"
                  placeholder="pass.com.yourshop.loyalty"
                  value={value.apple_wallet_pass_type_id}
                  onChange={(e) => onChange({ apple_wallet_pass_type_id: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-medium text-gray-600">Team ID</label>
                  <input
                    className="input-field text-sm w-full mt-1 font-mono"
                    placeholder="ABCDE12345"
                    value={value.apple_wallet_team_id}
                    onChange={(e) => onChange({ apple_wallet_team_id: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600">Organisation (optional)</label>
                  <input
                    className="input-field text-sm w-full mt-1"
                    placeholder="Defaults to shop name"
                    value={value.apple_wallet_org_name}
                    onChange={(e) => onChange({ apple_wallet_org_name: e.target.value })}
                  />
                </div>
              </div>
              <SecretField
                label="Pass certificate (PEM)"
                placeholder={
                  meta?.apple_wallet_cert_set
                    ? 'Leave blank to keep the saved certificate'
                    : '-----BEGIN CERTIFICATE-----'
                }
                value={value.apple_wallet_cert}
                isSet={meta?.apple_wallet_cert_set}
                clearing={value.clear_apple_wallet_cert}
                onChange={(v) => onChange({ apple_wallet_cert: v, clear_apple_wallet_cert: false })}
                onClear={() =>
                  onChange({ clear_apple_wallet_cert: true, apple_wallet_cert: '' })
                }
              />
              <SecretField
                label="Private key (PEM)"
                placeholder={
                  meta?.apple_wallet_key_set
                    ? 'Leave blank to keep the saved key'
                    : '-----BEGIN PRIVATE KEY-----'
                }
                value={value.apple_wallet_key}
                isSet={meta?.apple_wallet_key_set}
                clearing={value.clear_apple_wallet_key}
                onChange={(v) => onChange({ apple_wallet_key: v, clear_apple_wallet_key: false })}
                onClear={() => onChange({ clear_apple_wallet_key: true, apple_wallet_key: '' })}
              />
              <div>
                <label className="text-xs font-medium text-gray-600">
                  Key password {meta?.apple_wallet_key_password_set ? '(saved — leave blank to keep)' : '(optional)'}
                </label>
                <input
                  type="password"
                  className="input-field text-sm w-full mt-1"
                  autoComplete="new-password"
                  value={value.apple_wallet_key_password}
                  onChange={(e) => onChange({ apple_wallet_key_password: e.target.value })}
                />
              </div>
              <SecretField
                label="WWDR certificate (PEM)"
                placeholder={
                  meta?.apple_wallet_wwdr_set
                    ? 'Leave blank to keep the saved WWDR cert'
                    : '-----BEGIN CERTIFICATE-----'
                }
                value={value.apple_wallet_wwdr}
                isSet={meta?.apple_wallet_wwdr_set}
                clearing={value.clear_apple_wallet_wwdr}
                rows={3}
                onChange={(v) => onChange({ apple_wallet_wwdr: v, clear_apple_wallet_wwdr: false })}
                onClear={() =>
                  onChange({ clear_apple_wallet_wwdr: true, apple_wallet_wwdr: '' })
                }
              />
            </div>
          ) : null}
        </div>

        {/* Google */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Google Wallet (Android)</h4>
              <p className="text-[11px] text-gray-500">Requires Google Pay &amp; Wallet Console access</p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={value.google_wallet_enabled}
              onClick={() => onChange({ google_wallet_enabled: !value.google_wallet_enabled })}
              className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                value.google_wallet_enabled ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                  value.google_wallet_enabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          <Guide title="How to get Google credentials">
            <ol className="list-decimal pl-4 space-y-1.5">
              <li>
                Open the{' '}
                <a
                  className="text-blue-700 hover:underline inline-flex items-center gap-0.5"
                  href="https://pay.google.com/business/console"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Pay &amp; Wallet Console <ExternalLink className="w-3 h-3" />
                </a>{' '}
                and create / select an Issuer. Copy the <strong>Issuer ID</strong>.
              </li>
              <li>
                In{' '}
                <a
                  className="text-blue-700 hover:underline inline-flex items-center gap-0.5"
                  href="https://console.cloud.google.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Cloud Console <ExternalLink className="w-3 h-3" />
                </a>
                , enable the <strong>Google Wallet API</strong> for your project.
              </li>
              <li>
                Create a <strong>service account</strong>, download its JSON key, then in the Wallet Console add that
                service account email as a user with admin access to your issuer.
              </li>
              <li>
                Paste the Issuer ID and the full JSON key below, enable Google Wallet, and save. Members on Android
                Chrome will see <strong>Add to Google Wallet</strong> on their loyalty card.
              </li>
            </ol>
          </Guide>

          {value.google_wallet_enabled ? (
            <div className="space-y-2.5">
              <div>
                <label className="text-xs font-medium text-gray-600">Issuer ID</label>
                <input
                  className="input-field text-sm w-full mt-1 font-mono"
                  placeholder="3388000000012345678"
                  value={value.google_wallet_issuer_id}
                  onChange={(e) => onChange({ google_wallet_issuer_id: e.target.value })}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Class suffix</label>
                <input
                  className="input-field text-sm w-full mt-1 font-mono"
                  placeholder="loyalty"
                  value={value.google_wallet_class_suffix}
                  onChange={(e) => onChange({ google_wallet_class_suffix: e.target.value })}
                />
                <p className="text-[10px] text-gray-400 mt-0.5">Letters, numbers, underscore only</p>
              </div>
              <SecretField
                label="Service account JSON"
                placeholder={
                  meta?.google_wallet_sa_json_set
                    ? 'Leave blank to keep the saved service account JSON'
                    : '{ "type": "service_account", ... }'
                }
                value={value.google_wallet_sa_json}
                isSet={meta?.google_wallet_sa_json_set}
                clearing={value.clear_google_wallet_sa_json}
                rows={6}
                onChange={(v) =>
                  onChange({ google_wallet_sa_json: v, clear_google_wallet_sa_json: false })
                }
                onClear={() =>
                  onChange({ clear_google_wallet_sa_json: true, google_wallet_sa_json: '' })
                }
              />
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export const emptyWalletForm = (): LoyaltyWalletForm => ({
  apple_wallet_enabled: false,
  apple_wallet_pass_type_id: '',
  apple_wallet_team_id: '',
  apple_wallet_org_name: '',
  apple_wallet_cert: '',
  apple_wallet_key: '',
  apple_wallet_key_password: '',
  apple_wallet_wwdr: '',
  clear_apple_wallet_cert: false,
  clear_apple_wallet_key: false,
  clear_apple_wallet_wwdr: false,
  google_wallet_enabled: false,
  google_wallet_issuer_id: '',
  google_wallet_class_suffix: 'loyalty',
  google_wallet_sa_json: '',
  clear_google_wallet_sa_json: false,
});
